import cookie from '../cookie'
import { JWTDecode } from '@common/utils'
import { constants } from '@common/services'

export default class Auth {
  constructor(api) {
    this.api = api
  }

  login = async (ConsumerId, Password, TDCampaigns) => {
    const res = await this.api.post('/authentication/login', {
      data: {
        ConsumerId,
        Password,
        TDCampaigns
      },
      headers: {
        ...cookie.tokenHeader()
      }
      // mock: 'login.json'
    })
    return res
  }

  loginV2 = async (ConsumerId, Password, TDCampaigns) => {
    const res = await this.api.post('/authentication/loginv2', {
      data: {
        ConsumerId,
        Password,
        TDCampaigns
      },
      withCredentials: true,
      useProxy: true,
      headers: {
        ...cookie.tokenHeader()
      },
      mock: 'loginV2.json'
    })
    return res
  }

  syncSSO = async token => {
    const res = await this.api.post(
      `${constants.IQOSPHERE_BASE_URL}/api/jwt/sync`,
      {
        data: {
          jwt: token
        },
        timeout: Number(constants.SSO_SESSION_TIMEOUT) || 10000,
        withCredentials: true,
        useProxy: false,
        headers: {
          'application-id': null,
          'application-key': null
        },
        mock: 'syncSSO.json'
      }
    )
    return res
  }

  makeLoginUsingSSOEndpoint = async token => {
    const res = await this.api.post('/authentication/loginv2session', {
      data: {
        token
      },
      useProxy: true,
      withCredentials: true,
      mock: 'loginV2Session.json'
    })
    return res
  }

  syncLogout = async token => {
    const res = await this.api.post(
      `${constants.IQOSPHERE_BASE_URL}/api/jwt/sync?logout`,
      {
        data: {
          jwt: token
        },
        withCredentials: true,
        useProxy: true,
        headers: {
          'application-id': null,
          'application-key': null
        }
      }
    )
    return res
  }

  getCardBirthDate = async (data, isNHAV = false) => {
    const res = await this.api.post(
      '/account/ageverification?app=DCS-NGN' + (isNHAV ? '&folder=Final' : ''),
      {
        data,
        headers: { 'content-type': 'image/png' },
        mock: 'getCardBirthDate.json'
      }
    )
    return res
  }

  verifyAge = async (ocrToken, token) => {
    const res = await this.api.put(
      `/account/registration/verifyage?ocr=${ocrToken}`,
      {
        ...(token && { headers: { Authorization: `Bearer ${token}` } }),
        mock: 'verifyAge.json'
      }
    )
    return res
  }

  getAddConsumerFormData = async () => {
    const res = await this.api.get('/account/registration/formdata', {
      mock: 'getAddConsumerFormData.json'
    })
    return res
  }

  getAddressInfo = async opts => {
    const res = await this.api.get('/catalogue/addressinfo', {
      headers: {
        ...cookie.authorizationTokenHeader(opts)
      },
      ...opts
    })
    cookie.setToken(res.auth_token)
    return res
  }

  getPrefilledConsumerFormData = async opts => {
    const res = await this.api.get('/account/registration/formdata/contact', {
      headers: {
        ...cookie.authorizationTokenHeader(opts)
      },
      ...opts,
      mock: 'getPrefilledConsumerFormData.json'
    })
    return res
  }

  accountRegistration = async (data, token) => {
    const res = await this.api.post('/account/registration', {
      ...(token && { headers: { Authorization: `Bearer ${token}` } }),
      data,
      withCredentials: true,
      useProxy: true,
      mock: 'accountRegistration.json'
    })
    cookie.setToken(res.auth_token)
    // Necessary because we call this API without passing by the login process, which is the only place where we set the spiceid cookie
    const decodedToken = JWTDecode(res.auth_token)
    if (decodedToken) {
      cookie.setSpiceId(decodedToken.spice_id)
    }
    return res
  }

  requestEmailVerificationPINRegistration = async (
    emailAddress,
    action,
    token
  ) => {
    const res = await this.api.post('/account/registration/request/pin/email', {
      headers: {
        ...cookie.authorizationTokenHeader()
      },
      data: {
        emailAddress,
        action,
        token
      },
      mock: 'requestEmailVerificationPINRegistration.json'
    })
    cookie.setToken(res.auth_token)
    return res
  }

  requestEmailVerificationPIN = async (email_address, newAddress) => {
    const res = await this.api.post(
      newAddress
        ? '/profile/request/pin/email'
        : '/profile/verify/request/email',
      {
        headers: {
          ...cookie.authorizationTokenHeader()
        },
        ...(newAddress && {
          data: {
            emailAddress: email_address
          }
        }),
        mock: 'requestEmailVerificationPIN.json'
      }
    )
    cookie.setToken(res.auth_token)
    return res
  }

  verifyEmailPin = async verification_code => {
    const res = await this.api.post('/VerifyEmailPin', {
      headers: {
        ...cookie.authorizationTokenHeader()
      },
      withCredentials: true,
      useProxy: true,
      data: {
        verification_code
      },
      mock: 'verifyEmailPin.json'
    })
    cookie.setToken(res.auth_token)
    return res
  }

  requestSMSVerificationPINRegistration = async data => {
    const res = await this.api.post('/account/registration/request/pin/sms', {
      headers: {
        ...cookie.authorizationTokenHeader()
      },
      data,
      mock: 'requestSMSVerificationPINRegistration.json'
    })
    cookie.setToken(res.auth_token)
    return res
  }

  requestSMSVerificationPIN = async (phone_number, newPhone) => {
    const res = await this.api.post(
      newPhone ? '/profile/request/pin/sms' : '/profile/verify/request/phone',
      {
        headers: {
          ...cookie.authorizationTokenHeader()
        },
        ...(newPhone && {
          data: {
            phoneNumber: phone_number
          }
        }),
        mock: 'requestSMSVerificationPIN.json'
      }
    )
    cookie.setToken(res.auth_token)
    return res
  }

  requestVoiceVerificationPINRegistration = async data => {
    const res = await this.api.post('/account/registration/request/pin/tts', {
      headers: {
        ...cookie.authorizationTokenHeader()
      },
      data,
      mock: 'requestVoiceVerificationPINRegistration.json'
    })
    cookie.setToken(res.auth_token)
    return res
  }

  requestVoiceVerificationPIN = async (phone_number, newPhone) => {
    const res = await this.api.post(
      newPhone
        ? '/profile/request/pin/tts'
        : '/profile/verify/request/phone/tts',
      {
        headers: {
          ...cookie.authorizationTokenHeader()
        },
        ...(newPhone && {
          data: {
            phoneNumber: phone_number
          }
        }),
        mock: 'requestVoiceVerificationPIN.json'
      }
    )
    cookie.setToken(res.auth_token)
    return res
  }

  verifyPhonePin = async verification_code => {
    const res = await this.api.post('/VerifyPhonePin', {
      headers: {
        ...cookie.authorizationTokenHeader()
      },
      withCredentials: true,
      useProxy: true,
      data: {
        verification_code
      },
      mock: 'verifyPhonePin.json'
    })
    cookie.setToken(res.auth_token)
    return res
  }

  docomoRegistration = async ({ code, state, source }) => {
    const res = await this.api.post('/account/registration/docomo', {
      data: {
        code,
        state,
        source
      }
    })
    cookie.setToken(res.auth_token)
    return res
  }

  accountPasswordReset = async (loginname, birthday, recaptcha = {}) => {
    const res = await this.api.post('/account/password/reset', {
      data: {
        loginname,
        birthday,
        token: recaptcha.token,
        action: recaptcha.action
      },
      mock: 'accountPasswordReset.json'
    })
    return res
  }

  accountLoginnameRecover = async (email, recaptcha = {}) => {
    const res = await this.api.post('/account/loginname/recover', {
      data: {
        email,
        token: recaptcha.token,
        action: recaptcha.action
      },
      mock: 'accountLoginnameRecover.json'
    })
    return res
  }

  accountPasswordChange = async (password, token) => {
    const res = await this.api.post('/account/password/change', {
      data: {
        token,
        password
      },
      mock: 'accountPasswordChange.json'
    })
    return res
  }
}
